import { useQuery } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';

/**
 * Managed Kubernetes. Clusters live on the legacy v2 API (provider s10015 under
 * `Internal.Opennebula.Kubernetes`); v3 only carries the service subscription
 * (id == service_uuid), so we fan out to v2 per service. We parse the rich
 * cluster metadata (version, endpoint, pools, addons) and pull live node/pod/CPU/
 * memory from the per-cluster `/metrics` endpoint — mirrors the legacy console's
 * "Kubernetes Resources" section.
 */

export const KUBERNETES_SCOPE = 'Internal.Opennebula.Kubernetes';

export interface K8sPool {
  name: string;
  roles: string;
  cores: number;
  memory: number;
  volumes: number;
  nodes: number;
}

export interface K8sCluster {
  id: string;
  name: string;
  status: string;
  version: string;
  endpoint: { fqdn?: string; external?: string; internal?: string };
  cni: string;
  pools: K8sPool[];
  addons: string[];
}

export interface KubernetesSummary {
  services: number;
  clusters: number;
  running: number;
  /** Live counts from the first cluster's /metrics (falls back to pool spec). */
  nodes: number;
  pods: number;
  cpuCores: number;
  memoryGB: number;
  firstCluster?: K8sCluster;
  clusterList: K8sCluster[];
}

interface RawMachine {
  cores?: number;
  memory?: number;
}
interface RawPool {
  name?: string;
  roles?: string;
  cores?: number;
  memory?: number;
  volumes?: number;
  machines?: RawMachine[];
}
interface RawCluster {
  id?: string;
  name?: string;
  status?: string;
  state?: string;
  metadata?: {
    version?: string;
    runtime?: string;
    endpoint?: { fqdn?: string; external?: string; internal?: string };
    network?: { cni?: string };
    pools?: RawPool[];
    addons?: Record<string, { enabled?: boolean }>;
  };
}
interface ClustersEnvelope {
  data?: { clusters?: RawCluster[] };
}
interface MetricsEnvelope {
  data?: {
    nodes?: { usage?: { cpu?: string; memory?: string } }[];
    pods?: unknown[];
  };
}

function parseCluster(c: RawCluster): K8sCluster {
  const md = c.metadata ?? {};
  const pools: K8sPool[] = (md.pools ?? []).map((p) => ({
    name: p.name ?? '',
    roles: p.roles ?? 'worker',
    cores: p.cores ?? 0,
    memory: p.memory ?? 0,
    volumes: p.volumes ?? 0,
    nodes: p.machines?.length ?? 0,
  }));
  const addons = Object.entries(md.addons ?? {})
    .filter(([, v]) => v?.enabled)
    .map(([k]) => k);
  return {
    id: c.id ?? '',
    name: c.name ?? '',
    status: (c.status || c.state || 'unknown').toLowerCase(),
    version: md.version ?? '—',
    endpoint: md.endpoint ?? {},
    cni: md.network?.cni ?? '',
    pools,
    addons,
  };
}

export function useKubernetesSummary(serviceIds: string[]) {
  const key = [...serviceIds].sort().join(',');
  return useQuery<KubernetesSummary>({
    queryKey: ['v2', 'kubernetes', 'summary', key],
    enabled: serviceIds.length > 0,
    staleTime: 60_000,
    queryFn: async () => {
      const lists = await Promise.all(
        serviceIds.map((id) =>
          serviceApiV3
            .get<ClustersEnvelope>(`/services/${id}/s10015/clusters`)
            .then((r) => ({ id, clusters: r.data?.data?.clusters ?? [] }))
            .catch(() => ({ id, clusters: [] as RawCluster[] })),
        ),
      );

      const clusterList: K8sCluster[] = [];
      let running = 0;
      let firstServiceId: string | undefined;
      let firstRawId: string | undefined;
      for (const { id, clusters } of lists) {
        for (const raw of clusters) {
          const parsed = parseCluster(raw);
          clusterList.push(parsed);
          if (parsed.status === 'active' || parsed.status === 'running' || parsed.status === 'ready') running += 1;
          if (!firstRawId) {
            firstServiceId = id;
            firstRawId = parsed.id;
          }
        }
      }

      const first = clusterList[0];
      // Spec fallback (workers from pool machines).
      let nodes = first ? first.pools.reduce((s, p) => s + p.nodes, 0) : 0;
      let pods = 0;
      let cpuCores = first ? first.pools.reduce((s, p) => s + p.cores * p.nodes, 0) : 0;
      let memoryGB = first ? first.pools.reduce((s, p) => s + p.memory * p.nodes, 0) : 0;

      // Live metrics for the first cluster (includes masters + workers).
      if (firstServiceId && firstRawId) {
        try {
          const m = await serviceApiV3
            .get<MetricsEnvelope>(`/services/${firstServiceId}/s10015/clusters/${firstRawId}/metrics`)
            .then((r) => r.data?.data);
          if (m?.nodes?.length) {
            nodes = m.nodes.length;
            pods = m.pods?.length ?? 0;
            cpuCores =
              Math.round((m.nodes.reduce((s, n) => s + parseInt(n.usage?.cpu || '0', 10), 0) / 1e9) * 10) / 10;
            memoryGB =
              Math.round(
                (m.nodes.reduce((s, n) => s + parseInt((n.usage?.memory || '0').replace('Ki', ''), 10), 0) /
                  1024 /
                  1024) *
                  10,
              ) / 10;
          }
        } catch {
          /* metrics unavailable — keep spec fallback */
        }
      }

      return {
        services: serviceIds.length,
        clusters: clusterList.length,
        running,
        nodes,
        pods,
        cpuCores,
        memoryGB,
        firstCluster: first,
        clusterList,
      };
    },
  });
}
